import React, { useEffect, useState } from 'react';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as Icon from 'react-feather';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormToggle from '../../../components/form/FormToggle';
import { routeMethodOptions } from '../../../utils/common';

import {
  outboundAdd,
  outboundGetById,
  outboundUpdate,
  trunkOptionGet
} from '../../../store/slices/tenantSlices/configurationSlice';

const AddEditOutboundModal = ({
  isOpen = false,
  onCloseClick,
  isEdit = false,
  editRecord = null
}) => {
  const dispatch = useDispatch();

  const rulesInitialValue = {
    length: '',
    strip: '',
    prepand: '',
    type: 'single',
    from_caller_id: '',
    to_caller_id: '',
    trunk_id: ''
  };

  const [rulesDetails, setRulesDetails] = useState([rulesInitialValue]);
  const [trunkOptions, setTrunkOptions] = useState([]);

  const initialValues = {
    name: '',
    description: '',
    route_method: 'serial',
    status: 'true',
    rules: rulesDetails
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('name is required'),
    route_method: Yup.string().required('route method is required')
  });

  const onSubmit = async () => {
    if (isEdit) {
      try {
        const payload = { ...values, rules: [...rulesDetails], id: editRecord.id };
        const response = await dispatch(outboundUpdate(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          setRulesDetails([rulesInitialValue]);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const payload = { ...values, rules: [...rulesDetails] };
        const response = await dispatch(outboundAdd(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const fetchTrunkOption = async () => {
    try {
      const response = await dispatch(trunkOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setTrunkOptions(options);
      } else {
        setTrunkOptions([]);
      }
    } catch (error) {
      setTrunkOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchTrunkOption();
  }, []);

  const fetchOutboundDetails = async () => {
    try {
      const response = await dispatch(outboundGetById(editRecord.id)).unwrap();
      const outboundRoute = response?.outboundRoute || {};
      const rules =
        Array.isArray(response?.rules) && response.rules.length > 0
          ? [...response.rules]
          : [rulesInitialValue];

      setValues({
        ...outboundRoute,
        status: String(outboundRoute.status),
        rules
      });

      setRulesDetails(rules);
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchOutboundDetails();
    }
  }, [isEdit]);

  const handleRuleChange = (index, event) => {
    const { name, value } = event.target;
    const updatedRules = [...rulesDetails];
    updatedRules[index] = { ...updatedRules[index], [name]: value };
    if (name === 'type' && value === 'single') {
      updatedRules[index] = { ...updatedRules[index], to_caller_id: '' };
    }
    setRulesDetails(updatedRules);
  };

  const handleAddRule = () => {
    setRulesDetails([...rulesDetails, { ...rulesInitialValue }]);
  };

  const handleDeleteRule = (index) => {
    const updatedRules = rulesDetails.filter((_, i) => i !== index);
    setRulesDetails(updatedRules);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const reordered = Array.from(rulesDetails);
    const [movedItem] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, movedItem);

    setRulesDetails(reordered);
  };

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Outbound Routes' : 'Add Outbound Routes'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="crms" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormInput
                name="name"
                label="Name"
                placeholder="Enter name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormInput
                name="description"
                label="Description"
                placeholder="Enter Description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="route_method"
                label="Route Method"
                placeholder="Select Route Method"
                value={values.route_method}
                options={routeMethodOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="status"
                label="Status"
                value={values.status}
                onChange={(e) => setFieldValue('status', e)}
              />
            </Col>
          </Row>
          <Row>
            <Col lg="12" className="border-lightGray" style={{ borderBottom: '1px solid' }}>
              <h5 className="mt-4 text-primary fw-bolder">Rules Configuration</h5>
            </Col>
          </Row>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="rules-list">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {rulesDetails.map((rule, index, array) => (
                    <Draggable key={index} draggableId={`rule-${index}`} index={index}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className="d-flex align-items-center gap-3 border-bottom mb-2 pb-2">
                          <div {...provided.dragHandleProps} className="cursor-pointer">
                            <Icon.Menu size={18} className="text-info" />
                          </div>
                          <Row className="pt-3">
                            <Col lg="3">
                              <FormInput
                                name="length"
                                label="Length"
                                placeholder="Enter Length"
                                value={rule.length}
                                onChange={(e) => handleRuleChange(index, e)}
                              />
                            </Col>
                            <Col lg="3">
                              <FormInput
                                name="strip"
                                label="Strip"
                                placeholder="Enter Strip"
                                value={rule.strip}
                                onChange={(e) => handleRuleChange(index, e)}
                              />
                            </Col>
                            <Col lg="3">
                              <FormInput
                                name="prepand"
                                label="Prepend"
                                placeholder="Enter Prepend"
                                value={rule.prepand}
                                onChange={(e) => handleRuleChange(index, e)}
                              />
                            </Col>
                            <Col lg="3">
                              <FormSelect
                                name="type"
                                label="Type"
                                value={rule.type}
                                placeholder="Select Type"
                                options={[
                                  { label: 'Single', value: 'single' },
                                  { label: 'Range', value: 'range' }
                                ]}
                                onChange={(e) => handleRuleChange(index, e)}
                              />
                            </Col>
                            <Col lg="3">
                              <FormInput
                                name="from_caller_id"
                                label="From Caller ID"
                                placeholder="Enter From Caller ID"
                                value={rule.from_caller_id}
                                onChange={(e) => handleRuleChange(index, e)}
                              />
                            </Col>
                            {rule.type === 'range' && (
                              <Col lg="3">
                                <FormInput
                                  name="to_caller_id"
                                  label="To Caller ID"
                                  placeholder="Enter To Caller ID"
                                  value={rule.to_caller_id}
                                  onChange={(e) => handleRuleChange(index, e)}
                                />
                              </Col>
                            )}
                            <Col lg="3">
                              <FormSelect
                                name="trunk_id"
                                label="Trunk"
                                value={rule.trunk_id}
                                placeholder="Select Trunk"
                                options={trunkOptions}
                                onChange={(e) => handleRuleChange(index, e)}
                              />
                            </Col>
                            <Col lg="3" className="d-flex align-items-center gap-4 pt-2">
                              {array?.length > 1 ? (
                                <Icon.Trash
                                  size={20}
                                  onClick={() => handleDeleteRule(index)}
                                  className="cursor-pointer text-danger"
                                />
                              ) : null}
                              {array?.length - 1 === index ? (
                                <Icon.Plus
                                  className="cursor-pointer text-info"
                                  size={20}
                                  onClick={handleAddRule}
                                />
                              ) : null}
                            </Col>
                          </Row>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            setRulesDetails([rulesInitialValue]);
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="crms" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditOutboundModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditOutboundModal;
